import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import authApi from "../api/authApi";

const getErrorMessage = (err, fallback) => {
  const data = err?.data || err?.response?.data;
  if (data?.message) return data.message;
  if (Array.isArray(data?.errors) && data.errors[0]?.msg) return data.errors[0].msg;
  return fallback;
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [form, setForm] = useState({ password: "", confirmPassword: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState(!token ? "Invalid reset link." : "");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError("Password and confirm password do not match.");
      return;
    }

    setIsLoading(true);
    try {
      const data = await authApi.resetPasswordWithToken({
        token,
        password: form.password,
        confirmPassword: form.confirmPassword,
      });
      setMessage(data.message || "Password updated successfully.");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to reset password."));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Choose New Password | CaloriesPal</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          p: 2,
          background: "linear-gradient(135deg,#f8fafc,#e0f2fe)",
        }}
      >
        <Paper
          component="form"
          onSubmit={handleSubmit}
          elevation={0}
          sx={{
            width: "min(520px, 100%)",
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            boxShadow: "0 24px 70px rgba(15,23,42,0.16)",
          }}
        >
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Choose a new password
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Use at least 8 characters. After saving, you can sign in with the new password.
              </Typography>
            </Box>
            {message && <Alert severity="success">{message}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="New password"
              type="password"
              value={form.password}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, password: event.target.value }))
              }
              fullWidth
              required
              disabled={!token}
            />
            <TextField
              label="Confirm new password"
              type="password"
              value={form.confirmPassword}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, confirmPassword: event.target.value }))
              }
              fullWidth
              required
              disabled={!token}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isLoading || !token}
            >
              {isLoading ? "Updating..." : "Update password"}
            </Button>
            <Button onClick={() => navigate("/login")} sx={{ textTransform: "none" }}>
              Back to sign in
            </Button>
          </Stack>
        </Paper>
      </Box>
    </>
  );
};

export default ResetPassword;
